import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '#src/__generated__/globalTypes.ts';
import { Shards } from '#src/atoms/icons/Shards.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BrandL, BrandS } from '#src/atoms/typography/index.tsx';
import { MY_SORARE_TRANSACTIONS_ESSENCE } from '#src/constants/__generated__/routes.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useSeriesNav } from '#src/hooks/commonGame/useSeriesNav.ts';
import { usePinnedShardRarity } from '#src/hooks/config/usePinnedShardRarity.ts';
import { getShardColor } from '#src/set/index.ts';

import { CurrencyBalanceHeader } from '../CurrencyBalanceHeader.tsx';
import { ResponsiveRaritiesWrapper } from '../ResponsiveRaritiesWrapper.tsx';
import { ShardRarityItem } from './ShardRarityItem.tsx';
import { useFilteredShardsChests } from './useFilteredShardsChests.ts';

const SelectedCurrencyWrapper = styled(Vertical).attrs({
  gap: 2,
})`
  overflow: hidden;
  position: relative;
  padding: var(--double-unit) var(--unit) var(--unit) var(--unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--intermediate-unit);
`;

type Props = {
  onClick?: () => void;
};

export const ShardDropdownDetails = ({ onClick }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { pinnedShardRarity, pinnedShardCardSet } = usePinnedShardRarity();
  const filteredShardsChests = useFilteredShardsChests();
  const seriesNav = useSeriesNav();

  const { cardShardsChests } = currentUser || {};

  const pinnedChest = cardShardsChests?.find(
    ({ rarity, cardSet }) =>
      rarity === pinnedShardRarity &&
      (!cardSet || cardSet?.slug === pinnedShardCardSet)
  );
  const pinnedColor = getShardColor(
    pinnedShardRarity,
    pinnedChest?.cardSet?.branding
  );

  const shardsAmount = pinnedShardRarity
    ? pinnedChest?.cardShardsCount || 0
    : cardShardsChests
        ?.filter(({ rarity }) => rarity !== Rarity.common)
        .reduce((sum, { cardShardsCount }) => sum + cardShardsCount, 0) || 0;

  return (
    <SelectedCurrencyWrapper>
      <CurrencyBalanceHeader transactionsPath={MY_SORARE_TRANSACTIONS_ESSENCE}>
        <Vertical gap={0.5} center>
          <Shards size={2} color={pinnedColor} />
          <BrandS color={pinnedColor}>
            <FormattedMessage defaultMessage="Essence" />
          </BrandS>
          <BrandL>
            <FormattedNumber value={shardsAmount || 0} />
          </BrandL>
        </Vertical>
      </CurrencyBalanceHeader>
      {!seriesNav && (
        <ResponsiveRaritiesWrapper>
          {filteredShardsChests?.map(({ cardShardsCount, rarity, cardSet }) => {
            return (
              <ShardRarityItem
                key={`${rarity}${cardSet ? `:${cardSet.slug}` : ''}`}
                rarity={rarity}
                cardShardsCount={cardShardsCount}
                cardSet={cardSet}
                onClick={onClick}
              />
            );
          })}
        </ResponsiveRaritiesWrapper>
      )}
    </SelectedCurrencyWrapper>
  );
};
